import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppContext, defaultState } from "../../../app/store";
import {
  ActionType,
  PlanCategoryFilters,
  PlanSNPType,
  PlanType,
} from "../../../@types";
import { isMedicareAdvantageType, useTranslate } from "../../../helpers";
import { updateSNPFilters } from "./helpers";
import CarrierFilter from "./CarrierFilter";
import DrugCoverageFilter from "./DrugCoverageFilter";
import SpecialNeedsPlanFilter from "./SpecialNeedsPlansFilter";
import StarRatingsFilter from "./StarRatingsFilter";
import { HelpDrawerWrapper } from "../../../components";
import PlanCoveragesFilter from "./PlanCoveragesFilter";
import filterIcon from "../../../assets/filter.svg";
import PlanCategoriesFilter from "./PlanCategoriesFilter";

interface PlanFiltersHelpDrawerProps {
  availableCategories?: PlanCategoryFilters;
  resetToFirstPageOfResults: () => void;
  swapPlanType: () => void;
}

const PlanFiltersHelpDrawer: FunctionComponent<PlanFiltersHelpDrawerProps> = props => {
  const t = useTranslate();
  const context = useContext(AppContext);
  const {
    state: {
      searchResultsFilters = defaultState.searchResultsFilters,
      planType,
      snpFilters,
    },
    dispatch,
  } = context;
  const isMA = isMedicareAdvantageType(planType || PlanType.MAPD);
  const [unappliedFilters, setUnappliedFilters] = useState(
    searchResultsFilters
  );
  const [unappliedSnpFilters, setUnappliedSnpFilters] = useState(snpFilters);
  const [swapPlansOnApply, setSwapPlansOnApply] = useState(false);

  useEffect(() => {
    setUnappliedSnpFilters(snpFilters);
  }, [snpFilters]);

  useEffect(() => {
    setUnappliedFilters(searchResultsFilters);
  }, [searchResultsFilters]);

  useEffect(() => {
    if (swapPlansOnApply && planType !== PlanType.PDP) {
      dispatch({
        type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
        payload: unappliedFilters,
      });
      setSwapPlansOnApply(false);
    }
  }, [planType]);

  return (
    <HelpDrawerWrapper
      toggleText={
        <>
          <img
            src={filterIcon}
            height="16"
            className="ds-u-margin-right--1 ds-u-display--block ds-u-sm-display--none"
            alt=""
          />
          <span>{t("search_results_page.view_all_filters")}</span>
        </>
      }
      toggleClassName="FilterBar__toggle"
      drawerTitle={t("search_results_page.filter_plans")}
      footer={{
        id: "mobile-filters-actions",
        className: "PlanFilters__mobileActions",
        buttons: [
          {
            text: t("search_results_page.apply_filters"),
            className: "ds-u-margin-right--3",
            shouldCloseDrawer: true,
            onClick: () => {
              props.resetToFirstPageOfResults();
              if (swapPlansOnApply) {
                props.swapPlanType();
              } else {
                dispatch({
                  type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                  payload: {
                    ...unappliedFilters,
                  },
                });
                updateSNPFilters(context, unappliedSnpFilters || []);
              }
            },
          },
          {
            text: t("search_results_page.clear_all_filters"),
            variation: "ghost",
            className: "e2e-clear-filters ds-u-padding--1",
            onClick: () => {
              props.resetToFirstPageOfResults();
              dispatch({
                type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                payload: defaultState.searchResultsFilters,
              });
              updateSNPFilters(context, []);
            },
          },
        ],
      }}
    >
      <div className="mct-u-height--full">
        <PlanCoveragesFilter
          basicMode
          onFilterUpdate={(newFilterValue): void => {
            setUnappliedFilters({
              ...unappliedFilters,
              planCoveragesFilter: newFilterValue,
            });
          }}
        />

        <PlanCategoriesFilter
          basicMode
          categories={props.availableCategories}
          onFilterUpdate={(value, swapPlans): void => {
            setUnappliedFilters({
              ...unappliedFilters,
              planCategoriesFilter: value,
            });
            setSwapPlansOnApply(swapPlans || false);
          }}
        />

        <CarrierFilter
          basicMode
          value={unappliedFilters.insuranceCarrierFilter}
          options={unappliedFilters.insuranceCarriers.map(carrier => ({
            label: carrier,
            value: carrier,
          }))}
          onFilterUpdate={(newFilterValue): void => {
            setUnappliedFilters({
              ...unappliedFilters,
              insuranceCarrierFilter: newFilterValue,
            });
          }}
        />

        {isMA && (
          <DrugCoverageFilter
            basicMode
            onFilterUpdate={(newFilterValue): void => {
              setUnappliedFilters({
                ...unappliedFilters,
                drugCoverageFilter: newFilterValue,
              });
            }}
          />
        )}

        <StarRatingsFilter
          basicMode
          value={unappliedFilters.starRatingFilter}
          onFilterUpdate={(newFilterValue: string): void => {
            setUnappliedFilters({
              ...unappliedFilters,
              starRatingFilter: newFilterValue,
            });
          }}
        />

        {isMA && (
          <SpecialNeedsPlanFilter
            basicMode
            onFilterUpdate={(newFilterValues: string[]): void => {
              setUnappliedSnpFilters(
                newFilterValues.map(val => val as PlanSNPType)
              );
            }}
          />
        )}
      </div>
    </HelpDrawerWrapper>
  );
};

export default PlanFiltersHelpDrawer;
